import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionButton,
  DetailsList,
  DetailsListLayoutMode,
  MessageBar,
  MessageBarType,
  SelectionMode,
  Spinner,
  SpinnerSize,
  Stack,
  Text
} from '@fluentui/react';
import type { IColumn } from '@fluentui/react';
import * as smsApi from '../api';
import type { SmsEnrollmentByUser } from '../types';

interface SmsEnrollmentRegistryProps {
  onNotification?: (type: MessageBarType, message: string) => void;
}

const formatPhoneNumberForDisplay = (e164PhoneNumber: string): string => {
  if (!e164PhoneNumber) {
    return '';
  }

  const digitsOnly = e164PhoneNumber.replace(/\D/g, '');

  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return `(${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`;
  }

  if (digitsOnly.length === 10) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  }

  return e164PhoneNumber;
};

const SmsEnrollmentRegistry: React.FC<SmsEnrollmentRegistryProps> = ({ onNotification }) => {
  const [rows, setRows] = useState<SmsEnrollmentByUser[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await smsApi.getAllSmsEnrollments();
      setRows(data);
      setErrorMessage('');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load enrollment registry';
      setErrorMessage(message);
      if (onNotification) {
        onNotification(MessageBarType.error, message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [onNotification]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const columns = useMemo<IColumn[]>(
    () => [
      {
        key: 'userId',
        name: 'User',
        fieldName: 'userId',
        minWidth: 220,
        maxWidth: 340,
        isResizable: true,
        onRender: (item: SmsEnrollmentByUser) => item.userId
      },
      {
        key: 'phoneNumber',
        name: 'Phone Number',
        minWidth: 150,
        maxWidth: 200,
        isResizable: true,
        onRender: (item: SmsEnrollmentByUser) => formatPhoneNumberForDisplay(item.phoneNumber)
      },
      {
        key: 'status',
        name: 'Status',
        minWidth: 90,
        maxWidth: 120,
        onRender: (item: SmsEnrollmentByUser) => (
          <Text variant="small">
            {item.consentReceived ? (
              <span style={{ color: 'green' }}>Active</span>
            ) : (
              <span style={{ color: 'orange' }}>Pending</span>
            )}
          </Text>
        )
      }
    ],
    []
  );

  return (
    <Stack tokens={{ childrenGap: 12 }} styles={{ root: { paddingTop: 8 } }}>
      <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
        <Text variant="xLarge">Enrollment Registry</Text>
        <ActionButton iconProps={{ iconName: 'Refresh' }} onClick={() => void loadData()} disabled={isLoading}>
          Refresh
        </ActionButton>
      </Stack>

      <Text variant="small" styles={{ root: { color: '#666' } }}>
        Shows all registered on-call SMS numbers and whether each number is pending or active.
      </Text>

      {errorMessage && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline onDismiss={() => setErrorMessage('')}>
          {errorMessage}
        </MessageBar>
      )}

      {isLoading ? (
        <Stack horizontalAlign="center" styles={{ root: { padding: 24 } }}>
          <Spinner size={SpinnerSize.medium} label="Loading enrollment registry..." />
        </Stack>
      ) : rows.length === 0 ? (
        <Text variant="medium" styles={{ root: { fontStyle: 'italic', color: '#999' } }}>
          No active enrollments found.
        </Text>
      ) : (
        <DetailsList
          items={rows}
          columns={columns}
          selectionMode={SelectionMode.none}
          layoutMode={DetailsListLayoutMode.justified}
          styles={{
            root: {
              border: '1px solid #ddd',
              borderRadius: 4
            }
          }}
        />
      )}
    </Stack>
  );
};

export default SmsEnrollmentRegistry;
